import { Amount, formatMoneySimple } from '../utils/formatMoney'

/**
 * Indicateurs de seuil du tableau de bord.
 *
 * Les quatre jauges étaient des pavés en aplat bleu ou rouge, texte blanc et
 * statut en emoji. Elles criaient plus fort que le solde lui-même alors
 * qu'elles n'en sont que le contexte.
 *
 * Ici elles partagent une seule carte : surtitre, valeur de référence, piste
 * de progression fine, et une ligne d'état à pastille. La couleur ne sert
 * plus de fond — seulement à qualifier l'état, sur la pastille et la barre.
 */

const TONES = {
  positive: { text: 'text-positive', bar: 'bg-positive', badge: 'badge-positive' },
  negative: { text: 'text-negative', bar: 'bg-negative', badge: 'badge-negative' },
  warning: { text: 'text-warning', bar: 'bg-warning', badge: 'badge-warning' },
  neutral: { text: 'text-ink-muted', bar: 'bg-ink-disabled', badge: 'badge-neutral' },
}

const clamp = (n) => Math.max(0, Math.min(100, Number.isFinite(n) ? n : 0))

function GaugeCard({ title, status, tone = 'neutral', progress = null, primary, context }) {
  const t = TONES[tone] || TONES.neutral

  return (
    <div className="card flex h-full flex-col gap-3 p-5">
      <div className="flex items-start justify-between gap-3">
        <h3 className="overline">{title}</h3>
        {status && (
          <span className={t.badge}>
            <span className="dot" aria-hidden="true" />
            {status}
          </span>
        )}
      </div>

      {primary}

      {progress !== null && (
        <div
          className="h-1.5 w-full overflow-hidden rounded-full bg-surface-sunken"
          role="progressbar"
          aria-valuenow={Math.round(clamp(progress))}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={title}
        >
          <div
            className={`h-full rounded-full transition-[width] duration-500 ${t.bar}`}
            style={{ width: `${clamp(progress)}%` }}
          />
        </div>
      )}

      {context && <p className="mt-auto text-sm text-ink-muted">{context}</p>}
    </div>
  )
}

/** Carte de repli quand le seuil n'a pas été configuré dans les paramètres. */
function NotConfigured({ title, hint }) {
  return (
    <div className="card flex h-full flex-col gap-2 p-5">
      <h3 className="overline">{title}</h3>
      <p className="text-base text-ink-muted">{hint}</p>
    </div>
  )
}

export default function Gauge({ type, solde, params }) {
  const p = params && typeof params === 'object' ? params : { devise: 'EUR' }
  const devise = p.devise || 'EUR'
  const soldeNum = Number(solde) || 0

  const big = (value, tone) => (
    <Amount value={value} devise={devise} tone={tone} className="text-3xl font-semibold" />
  )

  if (type === 'etat-compte') {
    const plafond = parseFloat(p.montant_plafond) || 0
    const hasPlafond = plafond > 0
    const overPlafond = hasPlafond && soldeNum > plafond

    let tone = 'positive'
    let status = 'Positif'
    if (overPlafond) {
      tone = 'warning'
      status = 'Plafond dépassé'
    } else if (soldeNum < 0) {
      tone = 'negative'
      status = 'Négatif'
    } else if (soldeNum === 0) {
      tone = 'neutral'
      status = 'À zéro'
    }

    // Sans plafond configuré, la barre n'a pas de référence : on l'échelonne
    // sur une plage dynamique plutôt que d'afficher un pourcentage trompeur.
    let progress
    if (hasPlafond) {
      progress = soldeNum < 0 ? ((soldeNum + plafond) / (plafond * 2)) * 100 : (soldeNum / plafond) * 100
    } else {
      const maxRange = Math.max(2000, Math.abs(soldeNum) + 1000)
      progress = (Math.abs(soldeNum) / maxRange) * 100
    }

    return (
      <GaugeCard
        title="État du compte"
        status={status}
        tone={tone}
        progress={progress}
        primary={big(soldeNum, soldeNum < 0 ? 'negative' : 'none')}
        context={
          hasPlafond
            ? overPlafond
              ? `Plafond ${formatMoneySimple(plafond, devise)} — dépassé de ${formatMoneySimple(soldeNum - plafond, devise)}`
              : `Plafond ${formatMoneySimple(plafond, devise)}`
            : 'Aucun plafond défini'
        }
      />
    )
  }

  if (type === 'decouvert') {
    const max = parseFloat(p.montant_decouvert) || 0
    if (max <= 0) {
      return (
        <NotConfigured
          title="Découvert autorisé"
          hint="Aucun découvert autorisé sur vos comptes."
        />
      )
    }

    const enDecouvert = soldeNum < 0
    const utilise = enDecouvert ? Math.abs(soldeNum) : 0
    const disponible = Math.max(0, max - utilise)
    const progress = enDecouvert ? (utilise / max) * 100 : 0

    return (
      <GaugeCard
        title="Découvert autorisé"
        status={enDecouvert ? 'En découvert' : 'Non utilisé'}
        tone={enDecouvert ? 'negative' : 'positive'}
        progress={progress}
        primary={
          <div className="flex items-baseline gap-2">
            {big(enDecouvert ? utilise : disponible, enDecouvert ? 'negative' : 'none')}
            <span className="text-base text-ink-muted">
              {enDecouvert ? 'utilisés' : 'disponibles'}
            </span>
          </div>
        }
        context={`Sur ${formatMoneySimple(max, devise)} autorisés, tous comptes confondus`}
      />
    )
  }

  if (type === 'seuil') {
    const seuil = parseFloat(p.montant_seuil) || 0
    if (!seuil) {
      return (
        <NotConfigured
          title="Seuil d’alerte"
          hint="Non configuré. Définissez-le dans Paramètres pour être prévenu."
        />
      )
    }

    const ecart = soldeNum - seuil
    const isBelow = ecart < 0
    // La barre se lit par rapport au seuil : 50 % = pile sur le seuil.
    const progress = isBelow
      ? ((soldeNum - (seuil - 2000)) / 2000) * 50
      : 50 + (ecart / 2000) * 50

    return (
      <GaugeCard
        title="Seuil d’alerte"
        status={isBelow ? 'Sous le seuil' : 'Au-dessus'}
        tone={isBelow ? 'negative' : 'positive'}
        progress={progress}
        primary={
          <div className="flex items-baseline gap-2">
            <Amount
              value={Math.abs(ecart)}
              devise={devise}
              tone={isBelow ? 'negative' : 'positive'}
              className="text-3xl font-semibold"
            />
            <span className="text-base text-ink-muted">{isBelow ? 'manquants' : 'de marge'}</span>
          </div>
        }
        context={`Seuil fixé à ${formatMoneySimple(seuil, devise)}`}
      />
    )
  }

  if (type === 'minimum') {
    const minimum = parseFloat(p.montant_minimum) || 0
    if (!minimum) {
      return (
        <NotConfigured
          title="Solde minimum"
          hint="Non configuré. Définissez-le dans Paramètres pour suivre votre matelas."
        />
      )
    }

    const ecart = soldeNum - minimum
    const isAbove = ecart >= 0
    const progress = isAbove
      ? 50 + (ecart / 2000) * 50
      : ((soldeNum - (minimum - 2000)) / 2000) * 50

    return (
      <GaugeCard
        title="Solde minimum"
        status={isAbove ? 'Respecté' : 'Non atteint'}
        tone={isAbove ? 'positive' : 'negative'}
        progress={progress}
        primary={
          <div className="flex items-baseline gap-2">
            <Amount
              value={Math.abs(ecart)}
              devise={devise}
              tone={isAbove ? 'positive' : 'negative'}
              className="text-3xl font-semibold"
            />
            <span className="text-base text-ink-muted">{isAbove ? 'au-dessus' : 'en dessous'}</span>
          </div>
        }
        context={`Minimum fixé à ${formatMoneySimple(minimum, devise)}`}
      />
    )
  }

  return null
}
